import { useMemo, useState } from "react";
import { ChevronDown, Lightbulb, Sparkles } from "lucide-react";
import { Sheet } from "@/components/shell/Sheet";
import { Button } from "@/components/ui/Button";
import { Pill } from "@/components/ui/Pill";
import { MoneyText } from "@/components/ui/MoneyText";
import { cn } from "@/lib/utils/cn";
import { pluralizeZadania } from "@/lib/utils/plural";
import {
  CHORE_PRESETS,
  recommendPresetForAge,
  type ChorePreset,
  type ChorePresetChore,
  type ChorePresetId,
} from "@/lib/presets/chorePresets";

type Props = {
  open: boolean;
  /** Imię nowo dodanego dziecka — pokazujemy w nagłówku. */
  childName: string;
  /**
   * Wiek dziecka (opcjonalny) — gdy podany, auto-zaznacza wiekowo
   * dopasowany preset. Gdy `undefined`, użytkownik wybiera ręcznie.
   *
   * Caller (`AddChildSheet`) oblicza wiek z `member.birthYear` przez
   * `ageFromBirthYear()` z `@/lib/utils/dates`. Komponent zostaje
   * pure-presentation — nie wie o Firestore.
   */
  childAge?: number;
  /**
   * Akcja "Dodaj zaznaczone". Otrzymuje gotową listę chore'ów do utworzenia
   * (assignedTo/active/createdBy/createdAt nadaje parent kontener).
   */
  onConfirm: (chores: ChorePresetChore[]) => void | Promise<void>;
  /** Akcja "Pomiń" — zamyka sheet bez dodawania. */
  onSkip: () => void;
};

/**
 * Picker presetów chore'ów pokazywany **po** dodaniu dziecka. Rodzic widzi 4
 * gotowe zestawy (3 wiekowe + uniwersalny "czas poza ekranem"), wybiera jeden,
 * odznacza pojedyncze chore'y jeśli nie pasują, dodaje.
 *
 * Filozofia presetów — patrz `src/lib/presets/chorePresets.ts`.
 *
 * Komponent jest pure-presentation: NIE pisze do Firestore, NIE wie o
 * `householdId`/`uid`. Wywołuje `onConfirm(chores)` — to parent (AddChildSheet
 * w docelowej integracji) batchuje `createChore` z `assignedTo=childUid`.
 */
export function ChorePresetPicker({
  open,
  childName,
  childAge,
  onConfirm,
  onSkip,
}: Props) {
  // Preset wiekowy najlepiej pasujący — albo z childAge, albo null gdy wiek
  // nieznany. Otwieramy ten preset domyślnie. User może go zwinąć i otworzyć
  // inny ręcznie.
  const recommendedId: ChorePresetId | null =
    childAge != null ? recommendPresetForAge(childAge) : null;

  const [expandedId, setExpandedId] = useState<ChorePresetId | null>(
    recommendedId,
  );
  // Klucze odznaczonych chore'ów per preset. Key = `${presetId}::${index}`.
  // Trzymamy globalnie żeby user mógł rozwinąć inny preset, wrócić — i jego
  // wybór nadal był zachowany.
  const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set());

  const [busy, setBusy] = useState(false);

  const selectedChores: ChorePresetChore[] = useMemo(() => {
    if (!expandedId) return [];
    const preset = CHORE_PRESETS.find((p) => p.id === expandedId);
    if (!preset) return [];
    return preset.chores.filter(
      (_, idx) => !excludedKeys.has(`${expandedId}::${idx}`),
    );
  }, [expandedId, excludedKeys]);

  const toggleExpand = (id: ChorePresetId) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const toggleChore = (presetId: ChorePresetId, idx: number) => {
    const key = `${presetId}::${idx}`;
    setExcludedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selectedChores.length === 0) return;
    setBusy(true);
    try {
      await onConfirm(selectedChores);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onSkip}
      title={`Co ${childName} będzie robić?`}
      footer={
        <div className="grid grid-cols-[1fr_1.4fr] gap-2">
          <Button variant="secondary" onClick={onSkip} disabled={busy}>
            Pomiń
          </Button>
          <Button
            variant="accent"
            onClick={handleConfirm}
            disabled={busy || selectedChores.length === 0}
          >
            {busy
              ? "Dodaję…"
              : selectedChores.length === 0
                ? "Wybierz zestaw"
                : `Dodaj ${selectedChores.length} ${pluralizeZadania(selectedChores.length)}`}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-muted mb-4 leading-relaxed">
        Zacznijcie razem od gotowego zestawu — możesz odznaczyć co nie pasuje,
        a wszystko zmienisz później w „Zadania”.
      </p>

      <div className="space-y-3">
        {CHORE_PRESETS.map((preset) => (
          <PresetCard
            key={preset.id}
            preset={preset}
            expanded={expandedId === preset.id}
            isRecommended={preset.id === recommendedId}
            excludedKeys={excludedKeys}
            onToggleExpand={() => toggleExpand(preset.id)}
            onToggleChore={(idx) => toggleChore(preset.id, idx)}
          />
        ))}
      </div>
    </Sheet>
  );
}

type PresetCardProps = {
  preset: ChorePreset;
  expanded: boolean;
  isRecommended: boolean;
  excludedKeys: Set<string>;
  onToggleExpand: () => void;
  onToggleChore: (idx: number) => void;
};

function PresetCard({
  preset,
  expanded,
  isRecommended,
  excludedKeys,
  onToggleExpand,
  onToggleChore,
}: PresetCardProps) {
  const selectedCount = preset.chores.reduce(
    (acc, _, idx) =>
      acc + (excludedKeys.has(`${preset.id}::${idx}`) ? 0 : 1),
    0,
  );

  return (
    <div
      className={cn(
        "rounded-[18px] border bg-card overflow-hidden transition-colors",
        expanded ? "border-accent shadow-[0_0_0_3px_var(--kd-accent-soft)]" : "border-line",
      )}
    >
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <span
          aria-hidden
          className="shrink-0 w-10 h-10 rounded-full bg-cloud border border-line flex items-center justify-center text-xl"
        >
          {preset.emoji}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5 flex-wrap">
            <span className="font-display text-[15px] font-bold leading-tight">
              {preset.label}
            </span>
            {isRecommended && (
              <Pill tone="green" className="text-[10.5px] px-2 py-[2px]">
                <Sparkles className="h-3 w-3" />
                Polecane
              </Pill>
            )}
          </span>
          <span className="block text-[11.5px] text-muted mt-0.5">
            {selectedCount}/{preset.chores.length} zadań
          </span>
        </span>
        <ChevronDown
          className={cn(
            "shrink-0 h-4 w-4 text-muted transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>

      {expanded && (
        <div className="border-t border-line bg-cloud/40">
          <p className="px-4 pt-3 text-[12px] text-muted leading-relaxed">
            {preset.description}
          </p>
          <ul className="px-2 py-2 space-y-1">
            {preset.chores.map((chore, idx) => {
              const key = `${preset.id}::${idx}`;
              const excluded = excludedKeys.has(key);
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => onToggleChore(idx)}
                    aria-pressed={!excluded}
                    className={cn(
                      "w-full flex items-center gap-3 px-2 py-2 rounded-[12px] text-left transition-colors",
                      excluded
                        ? "opacity-50 hover:bg-cloud"
                        : "hover:bg-cloud",
                    )}
                  >
                    <span
                      className={cn(
                        "shrink-0 w-5 h-5 rounded-md border-[1.5px] flex items-center justify-center transition-colors",
                        excluded
                          ? "border-line bg-card"
                          : "border-accent bg-accent text-white",
                      )}
                      aria-hidden
                    >
                      {!excluded && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M2.5 6.5L5 9L9.5 3.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span
                      aria-hidden
                      className="shrink-0 w-8 h-8 rounded-[10px] bg-card border border-line flex items-center justify-center text-base"
                    >
                      {chore.icon ?? "✨"}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13px] font-semibold leading-tight">
                        {chore.title}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1.5">
                        {chore.kind === "family" ? (
                          <Pill tone="hay" className="text-[10px] px-1.5 py-[1px]">
                            💛 Pomoc
                          </Pill>
                        ) : (
                          <Pill tone="green" className="text-[10px] px-1.5 py-[1px]">
                            💰 Extra
                          </Pill>
                        )}
                        {chore.offline && (
                          <Pill tone="neutral" className="text-[10px] px-1.5 py-[1px]">
                            🌳 Offline
                          </Pill>
                        )}
                        <span className="text-[10.5px] text-muted">
                          {scheduleLabel(chore.schedule)}
                        </span>
                      </span>
                    </span>
                    {chore.kind === "extra" && (
                      <MoneyText
                        minor={chore.rewardMinor}
                        tone="money"
                        className="text-[13px] font-bold shrink-0"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="px-4 py-3 flex items-center gap-2 text-[11px] text-muted">
            <Lightbulb className="h-3.5 w-3.5" />
            <span>
              Domyślnie wszystkie zaznaczone. Tap żeby odznaczyć co nie pasuje.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function scheduleLabel(schedule: ChorePresetChore["schedule"]): string {
  if (schedule.type === "daily") return "Codziennie";
  if (schedule.type === "weekly") {
    const days = schedule.daysOfWeek
      .slice()
      .sort((a, b) => a - b)
      .map((d) => DAY_NAMES[d])
      .join(", ");
    return days;
  }
  return "Raz";
}

const DAY_NAMES = ["niedz.", "pn.", "wt.", "śr.", "czw.", "pt.", "sob."];
